import type { ParamType } from '@deepguide-ai/types'
import { format, isValid, parseISO } from 'date-fns'
import { DatePicker } from '@deepguide-ai/ui/date-picker'
import { SearchableSelect } from '@deepguide-ai/ui/combobox'
import { Input } from '@deepguide-ai/ui/input'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { Switch } from '@deepguide-ai/ui/switch'
import { cn } from '@/lib/utils'
import { BrowserProfileSelector } from '@/components/browser-profile/BrowserProfileSelector'

/** Sentinel stored for the "No Logged-In User" (fresh session) choice. */
export const FRESH_BROWSER_PROFILE = '__fresh__'

/**
 * Renders a single value input restricted to a declared `ParamType` — the one
 * place that maps a type to its widget (boolean→switch, enum→select, json/text→
 * textarea, number/date→typed input, url/email/string→text). Shared by the
 * runbook input form, the runbook param editor, the workflow schedule/param
 * editors, and the trigger editor's typed filter value, so the "best-effort
 * typing" behaviour stays identical everywhere.
 *
 * `onChange` echoes the widget's native value: boolean for `boolean`, otherwise
 * the string from the control (callers parse number/json/date as they already
 * do). `enum` falls back to a text input when no options are supplied yet.
 */
export interface TypedValueInputProps {
  type: ParamType
  value: unknown
  onChange: (value: unknown) => void
  /** Allowed values when `type === 'enum'`. */
  enumOptions?: string[]
  disabled?: boolean
  id?: string
  placeholder?: string
  /** Applied to the rendered control (input / select trigger / textarea). */
  className?: string
  /** Passed to the `browserProfile` selector as its description line. */
  description?: string
}

const asText = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : ''

/** The native `<input type>` for a (non-boolean/enum/json/date) `ParamType`. */
function inputType(type: ParamType): 'number' | 'text' {
  switch (type) {
    case 'number':
    case 'ordinal':
      return 'number'
    default:
      return 'text'
  }
}

function asDate(value: unknown): Date | undefined {
  if (value instanceof Date) return isValid(value) ? value : undefined
  if (typeof value !== 'string' || !value) return undefined
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : undefined
}

/** Serialize an arbitrary value for the JSON textarea. */
function asJsonText(value: unknown): string {
  if (typeof value === 'string') return value
  if (value === undefined || value === null) return ''
  return JSON.stringify(value, null, 2)
}

export function TypedValueInput({
  type,
  value,
  onChange,
  enumOptions,
  disabled,
  id,
  placeholder,
  className,
  description,
}: TypedValueInputProps) {
  const validEnumOptions = (enumOptions ?? []).filter(
    (option) => typeof option === 'string' && option.trim().length > 0
  )

  if (type === 'browserProfile') {
    const selectedProfileId =
      typeof value === 'string' && value ? value : FRESH_BROWSER_PROFILE
    return (
      <BrowserProfileSelector
        selectedProfileId={selectedProfileId}
        onProfileChange={(profileId) =>
          onChange(profileId === FRESH_BROWSER_PROFILE ? null : profileId)
        }
        showFreshProfile
        label='Browser identity'
        description={
          description ??
          'This procedure automates a browser. Pick a logged-in identity if the target site requires sign-in, or create one now.'
        }
      />
    )
  }

  if (type === 'boolean') {
    return (
      <div className='flex items-center gap-2'>
        <Switch
          id={id}
          checked={Boolean(value)}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked)}
        />
        <span className='text-[12px] text-text-body'>
          {value ? 'True' : 'False'}
        </span>
      </div>
    )
  }

  if (type === 'enum' && validEnumOptions.length > 0) {
    return (
      <SearchableSelect
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onValueChange={(next) => onChange(next)}
        options={validEnumOptions.map((option) => ({
          label: option,
          value: option,
        }))}
        placeholder={placeholder ?? 'Select value'}
        searchPlaceholder='Search values...'
        emptyMessage='No values found.'
        triggerProps={{
          id,
          className: cn('h-9 w-full text-[12px]', className),
        }}
      />
    )
  }

  if (type === 'text' || type === 'json') {
    return (
      <Textarea
        id={id}
        rows={type === 'text' ? 4 : undefined}
        value={type === 'json' ? asJsonText(value) : asText(value)}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className={cn(type === 'json' ? 'min-h-[90px] font-mono text-[12px]' : 'resize-y text-[12px]', className)}
        placeholder={placeholder ?? (type === 'json' ? '{"key": "value"}' : undefined)}
      />
    )
  }

  if (type === 'date') {
    return (
      <DatePicker
        id={id}
        value={asDate(value)}
        disabled={disabled}
        onChange={(date) =>
          onChange(date ? format(date, 'yyyy-MM-dd') : '')
        }
        className={cn('text-[12px]', className)}
        placeholder={placeholder}
      />
    )
  }

  return (
    <Input
      id={id}
      type={inputType(type)}
      value={asText(value)}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      className={cn('h-9 text-[12px]', className)}
      placeholder={placeholder}
    />
  )
}
